import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
	AdminPartnerPaymentRequestRow,
	GroupedStationRequestDetailsResponse,
} from "../../../../services/PartnerPaymentRequestService";
import Partner from "../../../../models/Partner";
import { useLanguage } from "../../../controls/layout/content/MultiLanguge";

export function formatPaymentRequestTime(time: string | null | undefined) {
	if (!time) return "-";
	const date = new Date(time);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
}

function getRequestTypeLabel(requestType: AdminPartnerPaymentRequestRow["requestType"]): string {
	if (requestType === "UNDER_MIN_PAYOUT") return "Below min payout";
	return "Above min payout";
}

interface BookingDisplayRow {
	orderId: number;
	paymentRequestId?: number;
	customerName: string | null;
	quantity: number | null;
	totalMoney: number | null;
	timeIn: string | null;
	timeOut: string | null;
}

function collectAllBookings(
	cycleDetails: GroupedStationRequestDetailsResponse | null | undefined,
	requests: AdminPartnerPaymentRequestRow[],
): BookingDisplayRow[] {
	if (cycleDetails?.bookingLines?.length) {
		return cycleDetails.bookingLines.map((bl) => ({
			orderId: bl.orderId,
			paymentRequestId: bl.paymentRequestId,
			customerName: bl.customerName,
			quantity: bl.quantity,
			totalMoney: bl.totalMoney,
			timeIn: bl.timeIn,
			timeOut: bl.timeOut,
		}));
	}
	if (cycleDetails?.items?.length) {
		return cycleDetails.items.flatMap((item) =>
			(item.orders || []).map((o) => ({
				orderId: o.id,
				paymentRequestId: item.id,
				customerName: o.customerName,
				quantity: o.quantity,
				totalMoney: o.totalMoney,
				timeIn: o.timeIn,
				timeOut: o.timeOut,
			})),
		);
	}
	return requests.flatMap((r) =>
		(r.orders || []).map((o) => ({
			orderId: o.id,
			paymentRequestId: r.id,
			customerName: o.customerName,
			quantity: o.quantity,
			totalMoney: o.totalMoney,
			timeIn: o.timeIn,
			timeOut: o.timeOut,
		})),
	);
}

function collectRequestBookings(
	request: AdminPartnerPaymentRequestRow,
	cycleDetails: GroupedStationRequestDetailsResponse | null | undefined,
): BookingDisplayRow[] {
	if (request.orders?.length) {
		return request.orders.map((o) => ({
			orderId: o.id,
			paymentRequestId: request.id,
			customerName: o.customerName,
			quantity: o.quantity,
			totalMoney: o.totalMoney,
			timeIn: o.timeIn,
			timeOut: o.timeOut,
		}));
	}
	const fromLines = cycleDetails?.bookingLines?.filter((bl) => bl.paymentRequestId === request.id);
	if (fromLines?.length) {
		return fromLines.map((bl) => ({
			orderId: bl.orderId,
			paymentRequestId: bl.paymentRequestId,
			customerName: bl.customerName,
			quantity: bl.quantity,
			totalMoney: bl.totalMoney,
			timeIn: bl.timeIn,
			timeOut: bl.timeOut,
		}));
	}
	const item = cycleDetails?.items?.find((i) => i.id === request.id);
	if (item?.orders?.length) {
		return item.orders.map((o) => ({
			orderId: o.id,
			paymentRequestId: request.id,
			customerName: o.customerName,
			quantity: o.quantity,
			totalMoney: o.totalMoney,
			timeIn: o.timeIn,
			timeOut: o.timeOut,
		}));
	}
	return [];
}

const HeaderRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding-bottom: 14px;
	border-bottom: 1px solid #e5e7eb;
	margin-bottom: 14px;
`;

const HeaderText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const Title = styled.h3`
	margin: 0;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
	line-height: 1.2;

	@media (max-width: 768px) {
		font-size: 16px;
	}
`;

const Subtitle = styled.p`
	margin: 0;
	font-size: 13px;
	color: #64748b;
`;

const BodyInner = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const SummaryGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 10px;

	@media (max-width: 1120px) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	@media (max-width: 920px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
`;

const SummaryCard = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 10px 12px;
	background: #f8fafc;
`;

const SummaryLabel = styled.div`
	font-size: 12px;
	color: #64748b;
	margin-bottom: 6px;
`;

const SummaryValue = styled.div`
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
`;

const SummaryValueLink = styled.button`
	font-size: 18px;
	font-weight: 700;
	color: #005e9e;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	text-decoration: underline;
	font-family: inherit;
	text-align: left;

	&:hover {
		color: #004a7a;
	}

	&:disabled {
		color: #0f172a;
		text-decoration: none;
		cursor: default;
	}
`;

const TableWrap = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	overflow: auto;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	min-width: 640px;
`;

const Th = styled.th`
	text-align: left;
	font-size: 12px;
	font-weight: 700;
	color: #475569;
	padding: 12px 14px;
	background-color: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
	white-space: nowrap;
`;

const Td = styled.td`
	font-size: 13px;
	color: #334155;
	padding: 12px 14px;
	border-bottom: 1px solid #f1f5f9;
	vertical-align: top;
`;

const BookingsLink = styled.button`
	background: none;
	border: none;
	padding: 0;
	font: inherit;
	color: #005e9e;
	font-weight: 700;
	cursor: pointer;
	text-decoration: underline;

	&:hover {
		color: #004a7a;
	}

	&:disabled {
		color: #334155;
		font-weight: 400;
		text-decoration: none;
		cursor: default;
	}
`;

const RequestText = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #0f172a;
`;

const MutedText = styled.div`
	font-size: 12px;
	color: #64748b;
	margin-top: 2px;
`;

const StatusBadge = styled.span<{ $background: string; $text: string; $border: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	background: ${(props) => props.$background};
	color: ${(props) => props.$text};
	border: 1px solid ${(props) => props.$border};
`;

const Dot = styled.span<{ $color: string }>`
	width: 7px;
	height: 7px;
	border-radius: 999px;
	background: ${(props) => props.$color};
	display: inline-block;
`;

const EmptyState = styled.div`
	padding: 22px;
	font-size: 14px;
	color: #64748b;
`;

const FooterNote = styled.div`
	font-size: 12px;
	color: #64748b;
`;

const BankInfoCard = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 12px 14px;
	background: #f8fafc;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const BankInfoTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #0f172a;
`;

const BankInfoGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;

	@media (max-width: 760px) {
		grid-template-columns: 1fr;
	}
`;

const BankInfoItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const BankInfoLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #64748b;
	text-transform: uppercase;
`;

const BankInfoValue = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
	word-break: break-word;
`;

const SubModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(15, 23, 42, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000002;
	padding: 20px;
`;

const SubModalPanel = styled.div`
	width: 920px;
	max-width: 96vw;
	max-height: 85vh;
	background: #ffffff;
	border-radius: 14px;
	box-shadow: 0 20px 45px rgba(15, 23, 42, 0.22);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const SubModalHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 16px 18px;
	border-bottom: 1px solid #e2e8f0;
	flex-shrink: 0;
`;

const SubModalTitle = styled.h4`
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	color: #0f172a;
`;

const SubModalClose = styled.button`
	border: none;
	background: transparent;
	color: #64748b;
	font-size: 22px;
	line-height: 1;
	cursor: pointer;
	padding: 4px;
	border-radius: 8px;
	flex-shrink: 0;

	&:hover {
		background-color: #f1f5f9;
		color: #334155;
	}
`;

const SubModalBody = styled.div`
	padding: 12px 16px 18px;
	overflow: auto;
`;

function pickDisplayCurrencyCode(
	partnerBank: Partner | null | undefined,
	cycleDetails: GroupedStationRequestDetailsResponse | null | undefined,
	requests: AdminPartnerPaymentRequestRow[],
	listRowCurrencyCode?: string | null,
): string | null {
	const fromList = `${listRowCurrencyCode ?? ""}`.trim();
	if (fromList) return fromList.toUpperCase();
	const fromPartner = `${partnerBank?.localCurrencyCode ?? ""}`.trim();
	if (fromPartner) return fromPartner.toUpperCase();
	const fromCycle = `${cycleDetails?.currencyCode ?? ""}`.trim();
	if (fromCycle) return fromCycle.toUpperCase();
	for (const req of requests) {
		const fromStation = `${req.station?.currencyCode ?? ""}`.trim();
		if (fromStation) return fromStation.toUpperCase();
	}
	return null;
}

const getCurrencyLabel = (currencyCode: string) => {
	if (currencyCode === "VND") return "VNĐ";
	return currencyCode;
};

const getFractionDigits = (currencyCode: string) => {
	return currencyCode === "VND" ? 0 : 2;
};

/** Used in this module and exported for other Debt Management tables that share the same rules (e.g. VND → 0 decimals + VNĐ). */
export function formatPartnerPayoutMoney(value: number | null | undefined, currencyCode?: string | null) {
	const amount = Number(value);
	if (!Number.isFinite(amount)) return "-";
	const trimmed = `${currencyCode ?? ""}`.trim();
	if (!trimmed) {
		return amount.toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}
	const normalizedCurrencyCode = trimmed.toUpperCase();
	const fractionDigits = getFractionDigits(normalizedCurrencyCode);
	return (
		amount.toLocaleString("en-US", {
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		}) + ` ${getCurrencyLabel(normalizedCurrencyCode)}`
	);
}

const formatAmount = formatPartnerPayoutMoney;

/**
 * Customer order amounts (Order.totalMoney) are ALWAYS denominated in USD:
 * Stripe/PayPal charge in Currency.USD and Onepay multiplies by an exchange rate to reach VND.
 * The partner payout currency (VND/PHP/MYR…) is a different axis — never label totalMoney with it.
 */
export const ORDER_CURRENCY_CODE = "USD";

const normalizeStatus = (status: string | null | undefined) => String(status || "").toUpperCase();

const getStatusMeta = (status: string) => {
	const normalized = normalizeStatus(status);
	if (normalized === "PAID") {
		return {
			label: "Paid",
			background: "#ECFDF3",
			text: "#067647",
			border: "#A6F4C5",
		};
	}
	if (normalized === "IN_TRANSIT") {
		return {
			label: "In Transit",
			background: "#FFFAEB",
			text: "#B54708",
			border: "#FEDF89",
		};
	}
	if (normalized === "PENDING" || normalized === "REQUESTED") {
		return {
			label: "Requested",
			background: "#EFF6FF",
			text: "#1D4ED8",
			border: "#BFDBFE",
		};
	}
	if (normalized === "REJECTED") {
		return {
			label: "Rejected",
			background: "#FEF2F2",
			text: "#B91C1C",
			border: "#FECACA",
		};
	}
	return {
		label: normalized || "-",
		background: "#F3F4F6",
		text: "#374151",
		border: "#E5E7EB",
	};
};

export interface PartnerPayoutDetailsContentProps {
	stationName: string;
	/** Payment cycle scope for this view (not all cycles for the partner). */
	cycleLabel?: string | null;
	/** When set with vnYear/vnMonth, subtitle shows Cycle + Month separately. */
	cyclePeriod?: 1 | 2 | null;
	vnYear?: number | null;
	vnMonth?: number | null;
	requests: AdminPartnerPaymentRequestRow[];
	formatTime?: (time: string | null | undefined) => string;
	partnerBankDetail?: Partner | null;
	/** Raw cycle payload: booking lines and items for drill-down lists. */
	cycleDetails?: GroupedStationRequestDetailsResponse | null;
	/** Currency from grouped list row when cycle payload omits partner localCurrencyCode (fallback for formatting). */
	listRowCurrencyCode?: string | null;
	/** e.g. modal close button */
	headerAction?: React.ReactNode;
	/** Live debt summary from station (optional — when parent fetches `/partner-debt-summary` equivalent). */
	stationDebtSummary?: {
		withdrawableAmount?: number;
		reservedAmount?: number;
		minBalance?: number;
		currencyCode?: string;
		minBalanceEnabled?: boolean;
	} | null;
}

function formatCycleMonthSubtitle(vnYear: number, vnMonth: number) {
	const idx = vnMonth - 1;
	const monthShort =
		["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][idx] || "";
	return `${monthShort} ${vnYear}`;
}

type BookingsModalState =
	| { open: false }
	| { open: true; title: string; rows: BookingDisplayRow[]; showRequestColumn: boolean };

function BookingsDetailSubModal(props: {
	state: BookingsModalState;
	onClose: () => void;
	currencyCode: string | null;
	formatTime: (time: string | null | undefined) => string;
}) {
	const { state, onClose, currencyCode, formatTime } = props;
	if (!state.open) return null;

	return createPortal(
		<SubModalOverlay
			onClick={onClose}
			role="presentation">
			<SubModalPanel onClick={(e) => e.stopPropagation()}>
				<SubModalHeader>
					<SubModalTitle>{state.title}</SubModalTitle>
					<SubModalClose
						type="button"
						onClick={onClose}
						aria-label="Close">
						×
					</SubModalClose>
				</SubModalHeader>
				<SubModalBody>
					{state.rows.length === 0 ? (
						<EmptyState>No booking rows returned for this selection.</EmptyState>
					) : (
						<TableWrap>
							<Table>
								<thead>
									<tr>
										{state.showRequestColumn ? <Th>Request</Th> : null}
										<Th>Booking</Th>
										<Th>Customer</Th>
										<Th>Quantity</Th>
										<Th>Amount</Th>
										<Th>Check-in</Th>
										<Th>Check-out</Th>
									</tr>
								</thead>
								<tbody>
									{state.rows.map((row, idx) => (
										<tr key={`${row.orderId}-${row.paymentRequestId ?? "x"}-${idx}`}>
											{state.showRequestColumn ? (
												<Td>
													{row.paymentRequestId != null ? `#${row.paymentRequestId}` : "—"}
												</Td>
											) : null}
											<Td>#{row.orderId}</Td>
											<Td>{row.customerName || "—"}</Td>
											<Td>{row.quantity != null ? row.quantity : "—"}</Td>
											<Td>{formatAmount(row.totalMoney, ORDER_CURRENCY_CODE)}</Td>
											<Td>{formatTime(row.timeIn)}</Td>
											<Td>{formatTime(row.timeOut)}</Td>
										</tr>
									))}
								</tbody>
							</Table>
						</TableWrap>
					)}
				</SubModalBody>
			</SubModalPanel>
		</SubModalOverlay>,
		document.body,
	);
}

export default function PartnerPayoutDetailsContent({
	stationName,
	cycleLabel,
	cyclePeriod,
	vnYear,
	vnMonth,
	requests,
	formatTime = formatPaymentRequestTime,
	partnerBankDetail,
	cycleDetails = null,
	listRowCurrencyCode = null,
	headerAction,
	stationDebtSummary = null,
}: PartnerPayoutDetailsContentProps) {
	const { lang } = useLanguage();
	const detailLinkLabel = lang === "vi" ? "Chi tiết" : "Details";
	const [bookingsModal, setBookingsModal] = React.useState<BookingsModalState>({ open: false });

	const summary = React.useMemo(() => {
		const totalRequests = requests.length;
		const totalBookings = requests.reduce((sum, r) => sum + (Number(r.orderCount) || 0), 0);
		const totalRequestedAmount = requests.reduce((sum, r) => sum + (Number(r.requestAmount) || 0), 0);
		const totalPaidAmount = requests.reduce((sum, r) => sum + (Number(r.paidAmount) || 0), 0);
		const outstandingAmount = totalRequestedAmount - totalPaidAmount;
		const totalInTransitAmount = requests
			.filter((r) => normalizeStatus(r.status) === "IN_TRANSIT")
			.reduce((sum, r) => sum + (Number(r.requestAmount) || 0), 0);

		return {
			totalRequests,
			totalBookings,
			totalRequestedAmount,
			totalPaidAmount,
			outstandingAmount,
			totalInTransitAmount,
		};
	}, [requests]);

	const allBookingRows = React.useMemo(
		() => collectAllBookings(cycleDetails ?? undefined, requests),
		[cycleDetails, requests],
	);

	const modalCurrencyCode = React.useMemo(
		() =>
			pickDisplayCurrencyCode(
				partnerBankDetail,
				cycleDetails ?? undefined,
				requests,
				listRowCurrencyCode,
			),
		[partnerBankDetail, cycleDetails, requests, listRowCurrencyCode],
	);

	const cycleSubtitle = React.useMemo(() => {
		if (
			cyclePeriod != null &&
			vnYear != null &&
			vnMonth != null &&
			Number.isFinite(vnYear) &&
			Number.isFinite(vnMonth) &&
			vnMonth >= 1 &&
			vnMonth <= 12
		) {
			const name = cyclePeriod === 1 ? "First Cycle" : "Second Cycle";
			const monthPart = formatCycleMonthSubtitle(vnYear, vnMonth);
			return { cycle: name, month: monthPart };
		}
		return null;
	}, [cyclePeriod, vnYear, vnMonth]);

	const latestTime = React.useMemo(() => {
		if (requests.length === 0) return "-";
		const latest = [...requests].sort((a, b) => {
			const ta = new Date(a.paidTime || a.requestTime).getTime();
			const tb = new Date(b.paidTime || b.requestTime).getTime();
			return tb - ta;
		})[0];
		return formatTime(latest?.paidTime || latest?.requestTime);
	}, [requests, formatTime]);

	const openAllBookings = React.useCallback(() => {
		setBookingsModal({
			open: true,
			title: `All bookings (${summary.totalBookings})`,
			rows: allBookingRows,
			showRequestColumn: true,
		});
	}, [allBookingRows, summary.totalBookings]);

	const totalBookingsInteractive = summary.totalBookings > 0;

	return (
		<>
			<BookingsDetailSubModal
				state={bookingsModal}
				onClose={() => setBookingsModal({ open: false })}
				currencyCode={modalCurrencyCode}
				formatTime={formatTime}
			/>

			<HeaderRow>
				<HeaderText>
					<Title>Payout details</Title>
					<Subtitle>
						{stationName}
						{cycleSubtitle
							? ` • Cycle: ${cycleSubtitle.cycle} • Month: ${cycleSubtitle.month}`
							: cycleLabel
							? ` • ${cycleLabel}`
							: ""}{" "}
						• Last updated: {latestTime}
					</Subtitle>
				</HeaderText>
				{headerAction ?? null}
			</HeaderRow>

			<BodyInner>
				<SummaryGrid>
					<SummaryCard>
						<SummaryLabel>Total requests</SummaryLabel>
						<SummaryValue>{summary.totalRequests}</SummaryValue>
					</SummaryCard>
					<SummaryCard>
						<SummaryLabel>Total bookings</SummaryLabel>
						{totalBookingsInteractive ? (
							<SummaryValueLink
								type="button"
								onClick={openAllBookings}>
								{summary.totalBookings} {detailLinkLabel}
							</SummaryValueLink>
						) : (
							<SummaryValue>{summary.totalBookings}</SummaryValue>
						)}
					</SummaryCard>
					<SummaryCard>
						<SummaryLabel>Total amount</SummaryLabel>
						<SummaryValue>{formatAmount(summary.totalRequestedAmount, modalCurrencyCode)}</SummaryValue>
					</SummaryCard>
					<SummaryCard>
						<SummaryLabel>Total in-transit</SummaryLabel>
						<SummaryValue>{formatAmount(summary.totalInTransitAmount, modalCurrencyCode)}</SummaryValue>
					</SummaryCard>
					<SummaryCard>
						<SummaryLabel>Total paid amount</SummaryLabel>
						<SummaryValue>{formatAmount(summary.totalPaidAmount, modalCurrencyCode)}</SummaryValue>
					</SummaryCard>
				</SummaryGrid>

				{(stationDebtSummary?.withdrawableAmount != null ||
					stationDebtSummary?.reservedAmount != null ||
					stationDebtSummary?.minBalance != null) &&
				stationDebtSummary?.minBalanceEnabled !== false ? (
					<SummaryGrid style={{ marginTop: 4 }}>
						{stationDebtSummary?.minBalance != null ? (
							<SummaryCard>
								<SummaryLabel>Reserve threshold (local)</SummaryLabel>
								<SummaryValue>
									{formatAmount(stationDebtSummary.minBalance, stationDebtSummary.currencyCode ?? modalCurrencyCode)}
								</SummaryValue>
							</SummaryCard>
						) : null}
						{stationDebtSummary?.reservedAmount != null ? (
							<SummaryCard>
								<SummaryLabel>Reserved in available</SummaryLabel>
								<SummaryValue>
									{formatAmount(stationDebtSummary.reservedAmount, stationDebtSummary.currencyCode ?? modalCurrencyCode)}
								</SummaryValue>
							</SummaryCard>
						) : null}
						{stationDebtSummary?.withdrawableAmount != null ? (
							<SummaryCard>
								<SummaryLabel>Withdrawable now</SummaryLabel>
								<SummaryValue>
									{formatAmount(stationDebtSummary.withdrawableAmount, stationDebtSummary.currencyCode ?? modalCurrencyCode)}
								</SummaryValue>
							</SummaryCard>
						) : null}
					</SummaryGrid>
				) : null}

				{partnerBankDetail?.minBalanceEnabled !== false &&
				partnerBankDetail?.minBalance != null &&
				Number(partnerBankDetail.minBalance) > 0 ? (
					<Subtitle style={{ marginTop: 8, marginBottom: 0 }}>
						Min balance reserve (USD, partner config):{" "}
						<b>{Number(partnerBankDetail.minBalance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
					</Subtitle>
				) : null}

				<BankInfoCard>
					<BankInfoTitle>Station bank information</BankInfoTitle>
					<BankInfoGrid>
						<BankInfoItem>
							<BankInfoLabel>Owner phone</BankInfoLabel>
							<BankInfoValue>
								{partnerBankDetail?.user?.phone || partnerBankDetail?.phone || "Not configured"}
							</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Bank name</BankInfoLabel>
							<BankInfoValue>{partnerBankDetail?.bankName || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Bank account number</BankInfoLabel>
							<BankInfoValue>{partnerBankDetail?.bankAccountNumber || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Account holder name</BankInfoLabel>
							<BankInfoValue>{partnerBankDetail?.bankAccountHolder || "Not configured"}</BankInfoValue>
						</BankInfoItem>
					</BankInfoGrid>
				</BankInfoCard>

				{requests.length === 0 ? (
					<EmptyState>No data available</EmptyState>
				) : (
					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Request</Th>
									<Th>Status</Th>
									<Th>Bookings</Th>
									<Th>Requested amount</Th>
									<Th>Request time</Th>
									<Th>Request type</Th>
								</tr>
							</thead>
							<tbody>
								{requests.map((request) => {
									const statusMeta = getStatusMeta(request.status);
									const requestCurrencyCode =
										`${request.station?.currencyCode ?? ""}`.trim().toUpperCase() ||
										modalCurrencyCode;
									const rowBookings = collectRequestBookings(request, cycleDetails ?? undefined);
									const bookingCount = Number(request.orderCount) || 0;
									const canOpenRowBookings = bookingCount > 0;

									return (
										<tr key={request.id}>
											<Td>
												<RequestText>Request #{request.id}</RequestText>
												<MutedText>Partner #{request.partnerId}</MutedText>
											</Td>
											<Td>
												<StatusBadge
													$background={statusMeta.background}
													$text={statusMeta.text}
													$border={statusMeta.border}>
													<Dot $color={statusMeta.text} />
													{statusMeta.label}
												</StatusBadge>
											</Td>
											<Td>
												{canOpenRowBookings ? (
													<BookingsLink
														type="button"
														onClick={() =>
															setBookingsModal({
																open: true,
																title: `Bookings — Request #${request.id}`,
																rows: rowBookings,
																showRequestColumn: false,
															})
														}>
														{bookingCount} {detailLinkLabel}
													</BookingsLink>
												) : (
													bookingCount
												)}
											</Td>
											<Td>{formatAmount(request.requestAmount, requestCurrencyCode)}</Td>
											<Td>{formatTime(request.requestTime)}</Td>
											<Td>{getRequestTypeLabel(request.requestType)}</Td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					</TableWrap>
				)}

				<FooterNote>
					Outstanding amount: {formatAmount(summary.outstandingAmount, modalCurrencyCode)}
				</FooterNote>
			</BodyInner>
		</>
	);
}
